import React, { useState, useEffect } from "react";
import "./resetPassword.css";
import { IoMdLock } from "react-icons/io";
import { CiCircleQuestion } from "react-icons/ci";
import { Eye, EyeOff, Lock } from "lucide-react";
import { IoMdCheckmarkCircle } from "react-icons/io";
import { MdCancel } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import password_mark from "../../assets/password_mark.png";
import { REST_API_URL } from "../../api/config";

const BASE_URL = REST_API_URL;

const getUserTimeZone = () => {
  if (typeof window === 'undefined') return 'UTC';
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const getUrlWithTimeZone = (baseUrl) => {
  const timeZone = getUserTimeZone();
  const separator = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${separator}timeZone=${encodeURIComponent(timeZone)}`;
};

export default function ResetPassword() {
  const [password, setPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [showRepeat, setShowRepeat] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [userTimeZone, setUserTimeZone] = useState("UTC");
  const navigate = useNavigate();

  useEffect(() => {
    const timeZone = getUserTimeZone();
    setUserTimeZone(timeZone);
    localStorage.setItem("user_timezone", timeZone);
  }, []);

  const email = localStorage.getItem("pendingEmail");
  const code = localStorage.getItem("recoveryCode");

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    
    if (/\s/.test(value)) {
      const passwordWithoutSpaces = value.replace(/\s/g, '');
      setPassword(passwordWithoutSpaces);
    } else {
      setPassword(value);
    }
  };

  const handlePasswordKeyDown = (e) => {
    if (e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      return;
    }
  };

  const handleRepeatPasswordChange = (e) => {
    const value = e.target.value;
    
    if (/\s/.test(value)) {
      const passwordWithoutSpaces = value.replace(/\s/g, '');
      setRepeatPassword(passwordWithoutSpaces);
    } else {
      setRepeatPassword(value);
    }
  };

  const handleRepeatPasswordKeyDown = (e) => {
    if (e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      return;
    }
  };

  const conditions = [
    { 
      label: "8 to 20 characters", 
      valid: password.length >= 8 && password.length <= 20 && !/\s/.test(password) 
    },
    { 
      label: "At least 1 number", 
      valid: /\d/.test(password) && !/\s/.test(password) 
    },
    { 
      label: "At least 1 upper case letter", 
      valid: /[A-Z]/.test(password) && !/\s/.test(password) 
    },
    { 
      label: "No spaces allowed", 
      valid: !/\s/.test(password) 
    },
  ];

  const isFormValid = conditions.every((c) => c.valid) && 
                      password === repeatPassword && 
                      !/\s/.test(password) && 
                      !/\s/.test(repeatPassword);

  const handleSubmit = async () => {
    if (!isFormValid || !email || !code) {
      setError("Please fill all fields correctly.");
      return;
    }

    if (/\s/.test(password) || /\s/.test(repeatPassword)) {
      setError("Password cannot contain spaces");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const resetUrl = getUrlWithTimeZone(`${BASE_URL}/auth/recovery-reset-password/`);
      const res = await fetch(resetUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          code,
          password,
          password2: repeatPassword,
          timeZone: userTimeZone,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        if (data?.password2?.length) {
          setError(data.password2[0]);
        } else if (data?.password?.length) {
          setError(data.password[0]);
        } else if (data?.detail || data?.error) {
          setError(data.detail || data.error);
        } else {
          setError("Password reset failed.");
        }
        return;
      }

      const loginUrl = getUrlWithTimeZone(`${BASE_URL}/auth/token/obtain/`);
      const loginRes = await fetch(loginUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          email, 
          password,
          timeZone: userTimeZone,
        }),
      });

      const loginData = await loginRes.json().catch(() => ({}));

      if (loginRes.ok && loginData?.access) {
        localStorage.setItem("accessToken", loginData.access);
        localStorage.setItem("refreshToken", loginData.refresh);
        
        localStorage.setItem("password_reset_time", new Date().toISOString());
        localStorage.setItem("password_reset_timezone", userTimeZone);
        
        localStorage.removeItem("recoveryCode");
        localStorage.removeItem("pendingEmail");
        localStorage.removeItem("recovery_request_time");
        localStorage.removeItem("recovery_user_timezone");
        
        navigate("/password-reset-success");
      } else {
        setError("Login failed. Please try again.");
      }
    } catch (err) {
      console.error("❌ RESET PASSWORD ERROR:", err);
      setError("Network error. Try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && isFormValid && !loading) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="password">
      <main className="password-main">
        <div className="password-card">
          <img src={password_mark} alt="password" className="password-avatar" />
          <h2 className="password-title">Reset Password</h2>

          <label className="password-label">Password *</label>
          <div className="input-wrapper">
            <Lock className="icon" />
            <input
              type={showPass ? "text" : "password"}
              placeholder="Enter password (no spaces)"
              value={password}
              onChange={handlePasswordChange}
              onKeyDown={(e) => {
                handlePasswordKeyDown(e);
                if (e.key === "Enter" && isFormValid && !loading) {
                  handleKeyDown(e);
                }
              }}
              autoFocus
            />
            <button
              type="button"
              className="eye-btn"
              onClick={() => setShowPass(!showPass)}
              tabIndex={-1}
            >
              {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          {password && (
            <ul className="password-conditions">
              {conditions.map((c, i) => (
                <li key={i} className={c.valid ? "valid" : "invalid"}>
                  {c.valid ? <IoMdCheckmarkCircle /> : <MdCancel />} {c.label}
                </li>
              ))}
            </ul>
          )}

          <label className="password-label">Repeat Password *</label>
          <div className="input-wrapper">
            <Lock className="icon" />
            <input
              type={showRepeat ? "text" : "password"}
              placeholder="Repeat password (no spaces)"
              value={repeatPassword}
              onChange={handleRepeatPasswordChange}
              onKeyDown={(e) => {
                handleRepeatPasswordKeyDown(e);
                if (e.key === "Enter" && isFormValid && !loading) {
                  handleKeyDown(e);
                }
              }}
            />
            <button
              type="button"
              className="eye-btn"
              onClick={() => setShowRepeat(!showRepeat)}
              tabIndex={-1}
            >
              {showRepeat ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

          {error && <p className="error-text">{String(error)}</p>}

          <button
            className="password-btn"
            disabled={!isFormValid || loading}
            onClick={handleSubmit}
          >
            {loading ? "Processing..." : "Reset Password"}
          </button>
        </div>
      </main>
    </div>
  );
}